import { useGymData, getAppLoginUrl } from './hooks/useGymData'

const CATEGORY_ICONS = {
  yoga: '🧘', pilates: '🤸', hiit: '🔥', spinning: '🚴', zumba: '💃',
  boxing: '🥊', crossfit: '🏋️', stretching: '🤾', dance: '💫',
  strength: '💪', cardio: '🏃', martial_arts: '🥋', other: '⚡',
}

const PRODUCT_CATEGORY_ICONS = {
  supplement: '💊', apparel: '👕', equipment: '🏋️', food: '🍎',
  drink: '🥤', accessory: '🧢', other: '🛍️',
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'short', month: 'short', day: 'numeric' })
}

function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20, background: '#fff' }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid #e5e7eb', borderTop: '3px solid #6366f1', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <p style={{ color: '#9ca3af', fontSize: 14 }}>Chargement…</p>
    </div>
  )
}

function NotFound({ slug }) {
  return (
    <div style={{ minHeight: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <p style={{ color: '#6366f1', fontSize: 48, margin: 0, fontWeight: 900 }}>404</p>
      <p style={{ color: '#fff', fontSize: 20, margin: 0, fontWeight: 700 }}>Site introuvable</p>
      {slug && <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, margin: 0 }}>« {slug} » n'existe pas.</p>}
    </div>
  )
}

function formatDuration(durationDays, billingCycle) {
  if (billingCycle) return billingCycle
  if (!durationDays) return ''
  if (durationDays <= 7)  return `${durationDays}j`
  if (durationDays <= 31) return `${Math.round(durationDays / 7)} sem`
  if (durationDays <= 93) return `${Math.round(durationDays / 30)} mois`
  return `${Math.round(durationDays / 365)} an`
}

// ── Shared nav phone button ─────────────────────────────────────────────────
function PhoneButton({ phone, primary, dark = false }) {
  if (!phone) return null
  return (
    <a href={`tel:${phone}`} style={{
      background: primary, color: dark ? '#000' : '#fff',
      padding: '9px 22px', borderRadius: 999,
      fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap',
      display: 'flex', alignItems: 'center', gap: 7,
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8a19.79 19.79 0 01-3.07-8.68A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92v2z"/>
      </svg>
      {phone}
    </a>
  )
}

// ── Staff/member login link ─────────────────────────────────────────────────
function LoginLink({ orgId, color }) {
  if (!orgId) return null
  return (
    <a href={getAppLoginUrl(orgId)} style={{ color, fontWeight: 600, whiteSpace: 'nowrap' }}>
      Connexion
    </a>
  )
}

// ── Logo / name badge ───────────────────────────────────────────────────────
function LogoBadge({ org, size = 36 }) {
  const initials = (org.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  if (org.logo) {
    return <img src={org.logo} alt={org.name} style={{ height: size, width: size, objectFit: 'contain', borderRadius: 8 }} />
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: 8, background: org.primaryColor ?? '#6366f1',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 800, fontSize: size * 0.38,
    }}>
      {initials}
    </div>
  )
}

// ── Opening hours helpers ───────────────────────────────────────────────────
const DAYS_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const DAY_FR = { mon: 'Lun', tue: 'Mar', wed: 'Mer', thu: 'Jeu', fri: 'Ven', sat: 'Sam', sun: 'Dim' }

function fmtMin(m) {
  const h = Math.floor(m / 60), mm = m % 60
  return `${String(h).padStart(2,'0')}:${String(mm).padStart(2,'0')}`
}

function parseHours(raw) {
  if (!raw) return null
  try {
    const p = JSON.parse(raw)
    if (p && typeof p === 'object' && 'mon' in p) return p
  } catch {}
  return raw // fallback: plain text
}

function OpeningHoursBlock({ raw, text, labelColor }) {
  const parsed = parseHours(raw)
  if (!parsed) return null

  // Structured JSON — render as day grid
  if (typeof parsed === 'object') {
    return (
      <div>
        {DAYS_ORDER.map(d => {
          const s = parsed[d]
          if (!s) return null
          return (
            <div key={d} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: text, marginBottom: 3 }}>
              <span style={{ fontWeight: 600, width: 36 }}>{DAY_FR[d]}</span>
              {s.closed
                ? <span style={{ color: '#9ca3af' }}>Fermé</span>
                : <span>{fmtMin(s.open)} – {fmtMin(s.close)}</span>}
            </div>
          )
        })}
      </div>
    )
  }

  // Plain text fallback
  return <p style={{ fontSize: 13, color: text, whiteSpace: 'pre-line' }}>{parsed}</p>
}

// ── Shared footer ───────────────────────────────────────────────────────────
function ContactFooter({ org, primary, dark = false }) {
  const text      = dark ? '#9ca3af' : '#6b7280'
  const border    = dark ? '#1f2937' : '#e5e7eb'
  const bg        = dark ? '#030712' : '#fff'
  const labelClr  = dark ? '#4b5563' : '#9ca3af'
  const hasHours  = !!org.openingHours
  const hasSocial = !!(org.instagram || org.facebook)

  return (
    <footer id="contact" style={{ background: bg, borderTop: `1px solid ${border}`, padding: '48px 0' }}>
      <div style={{
        maxWidth: 1152, margin: '0 auto', padding: '0 24px',
        display: 'grid',
        gridTemplateColumns: hasHours
          ? 'repeat(auto-fit, minmax(200px,1fr))'
          : 'repeat(3,1fr)',
        gap: 32,
      }}>
        {/* Contact */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: labelClr, marginBottom: 12 }}>Contact</p>
          {org.phone   && <p style={{ fontSize: 14, color: text, marginBottom: 4 }}>{org.phone}</p>}
          {org.email   && <p style={{ fontSize: 14, color: text, marginBottom: 4 }}>{org.email}</p>}
          {org.address && <p style={{ fontSize: 14, color: text }}>{org.address}</p>}
        </div>

        {/* Opening hours */}
        {hasHours && (
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: labelClr, marginBottom: 12 }}>Horaires</p>
            <OpeningHoursBlock raw={org.openingHours} text={text} labelColor={labelClr} />
          </div>
        )}

        {/* Social */}
        {hasSocial && (
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: labelClr, marginBottom: 12 }}>Suivez-nous</p>
            {org.instagram && <a href={`https://instagram.com/${org.instagram}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: text, display: 'block', marginBottom: 4 }}>Instagram @{org.instagram}</a>}
            {org.facebook  && <a href={`https://facebook.com/${org.facebook}`}  target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: text, display: 'block' }}>Facebook /{org.facebook}</a>}
          </div>
        )}

        {/* CTA */}
        <div style={{ textAlign: 'right', alignSelf: 'start' }}>
          {org.phone && (
            <a href={`tel:${org.phone}`} style={{ background: primary, display: 'inline-block', padding: '12px 24px', borderRadius: 999, color: dark ? '#000' : '#fff', fontWeight: 700, fontSize: 14 }}>
              Nous contacter
            </a>
          )}
        </div>
      </div>
    </footer>
  )
}

// ── MODERN ──────────────────────────────────────────────────────────────────
function ModernTemplate({ org, sessions, plans, products }) {
  const primary = org.primaryColor ?? '#6366f1'
  const ctaLabel = org.trialCtaLabel || 'Réserver un essai gratuit'
  const ctaHref  = org.trialCtaUrl  || `tel:${org.phone}`

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: 'Inter, Segoe UI, Arial, sans-serif' }}>
      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', borderBottom: `1px solid ${primary}22`, zIndex: 50 }}>
        <div style={{ maxWidth: 1152, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LogoBadge org={org} />
            <span style={{ fontWeight: 700, fontSize: 18, color: '#111' }}>{org.name}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <div style={{ display: 'flex', gap: 24, fontSize: 14, fontWeight: 500, color: '#4b5563' }}>
              {<a href="#classes" style={{ color: '#4b5563' }}>Cours</a>}
              <a href="#plans"   style={{ color: '#4b5563' }}>Tarifs</a>
              {products.length > 0 && <a href="#shop" style={{ color: '#4b5563' }}>Boutique</a>}
              <a href="#contact" style={{ color: '#4b5563' }}>Contact</a>
              <LoginLink orgId={org.id} color={primary} />
            </div>
            <PhoneButton phone={org.phone} primary={primary} />
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ maxWidth: 1152, margin: '0 auto', padding: '96px 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 52, fontWeight: 900, color: '#111', lineHeight: 1.1, marginBottom: 16 }}>{org.heroTitle || org.name}</h1>
        {org.heroSubtitle && <p style={{ fontSize: 20, color: '#6b7280', maxWidth: 640, margin: '0 auto 32px' }}>{org.heroSubtitle}</p>}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="#plans" style={{ background: primary, display: 'inline-block', padding: '14px 32px', borderRadius: 999, color: '#fff', fontWeight: 700, fontSize: 16 }}>
            Voir les abonnements
          </a>
          {org.trialCtaUrl && (
            <a href={ctaHref} style={{ display: 'inline-block', padding: '14px 32px', borderRadius: 999, color: primary, fontWeight: 700, fontSize: 16, border: `2px solid ${primary}` }}>
              {ctaLabel}
            </a>
          )}
        </div>
      </section>

      {/* About */}
      {org.about && (
        <section style={{ background: '#f9fafb', padding: '80px 0' }}>
          <div style={{ maxWidth: 768, margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111', marginBottom: 16 }}>À propos</h2>
            <p style={{ color: '#6b7280', lineHeight: 1.8, whiteSpace: 'pre-line' }}>{org.about}</p>
          </div>
        </section>
      )}

      {/* Classes */}
      {sessions.length > 0 && (
        <section id="classes" style={{ maxWidth: 1152, margin: '0 auto', padding: '80px 24px' }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111', marginBottom: 8 }}>Prochains cours</h2>
          <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 32 }}>14 prochains jours</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
            {sessions.slice(0, 12).map(s => (
              <div key={s.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 20 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{CATEGORY_ICONS[s.category] ?? '⚡'}</div>
                <p style={{ fontWeight: 700, color: '#111' }}>{s.className}</p>
                <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>{formatDate(s.date)} · {s.startTime}</p>
                <p style={{ fontSize: 12, color: '#d1d5db', marginTop: 4 }}>{s.durationMinutes} min · {s.bookedCount ?? 0}/{s.maxCapacity} places</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Plans */}
      {plans.length > 0 && (
        <section id="plans" style={{ background: '#f9fafb', padding: '80px 0' }}>
          <div style={{ maxWidth: 1152, margin: '0 auto', padding: '0 24px' }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111', marginBottom: 8, textAlign: 'center' }}>Abonnements</h2>
            <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 40, textAlign: 'center' }}>Choisissez le forfait qui vous convient</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 24 }}>
              {plans.map((p, i) => (
                <div key={p.id} style={{ background: '#fff', borderRadius: 16, padding: 24, textAlign: 'center', border: i === 1 ? `2px solid ${primary}` : '1px solid #e5e7eb' }}>
                  {i === 1 && <p style={{ color: primary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Le plus populaire</p>}
                  <p style={{ fontWeight: 700, color: '#111', fontSize: 18 }}>{p.name}</p>
                  <p style={{ fontSize: 32, fontWeight: 900, color: '#111', marginTop: 12 }}>{p.price} <span style={{ fontSize: 16, fontWeight: 500, color: '#9ca3af' }}>MAD</span></p>
                  <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>/ {formatDuration(p.durationDays, p.billingCycle)}</p>
                  {p.description && <p style={{ fontSize: 13, color: '#6b7280', marginTop: 12 }}>{p.description}</p>}
                  {org.phone && (
                    <a href={`tel:${org.phone}`} style={{ marginTop: 20, display: 'block', padding: '10px 0', borderRadius: 999, fontWeight: 600, fontSize: 14, background: i === 1 ? primary : 'transparent', color: i === 1 ? '#fff' : '#374151', border: i === 1 ? 'none' : '1px solid #d1d5db' }}>
                      Commencer
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Shop */}
      {products.length > 0 && (
        <section id="shop" style={{ maxWidth: 1152, margin: '0 auto', padding: '80px 24px' }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111', marginBottom: 8 }}>Boutique</h2>
          <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 32 }}>Disponible à la salle</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 16 }}>
            {products.map(p => (
              <div key={p.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 20, opacity: p.inStock ? 1 : 0.5 }}>
                {p.imageUrl ? (
                  <img src={p.imageUrl} alt={p.name} style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 10, marginBottom: 10 }} />
                ) : (
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{PRODUCT_CATEGORY_ICONS[p.category] ?? '🛍️'}</div>
                )}
                <p style={{ fontWeight: 700, color: '#111' }}>{p.name}</p>
                {p.brand && <p style={{ fontSize: 12, color: '#9ca3af' }}>{p.brand}</p>}
                <p style={{ fontSize: 18, fontWeight: 900, color: '#111', marginTop: 6 }}>{p.price} <span style={{ fontSize: 12, fontWeight: 500, color: '#9ca3af' }}>MAD</span></p>
                {!p.inStock && <p style={{ fontSize: 11, color: '#ef4444', fontWeight: 600, marginTop: 4 }}>Rupture de stock</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <ContactFooter org={org} primary={primary} />
    </div>
  )
}

// ── SPORT ───────────────────────────────────────────────────────────────────
function SportTemplate({ org, sessions, plans, products }) {
  const primary  = org.primaryColor ?? '#f59e0b'
  const ctaLabel = org.trialCtaLabel || 'Essai gratuit →'
  const ctaHref  = org.trialCtaUrl  || `tel:${org.phone}`

  return (
    <div style={{ minHeight: '100vh', background: '#030712', fontFamily: 'Inter, Segoe UI, Arial, sans-serif', color: '#fff' }}>
      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, background: 'rgba(3,9,18,0.95)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #1f2937', zIndex: 50 }}>
        <div style={{ maxWidth: 1152, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LogoBadge org={org} />
            <span style={{ fontWeight: 900, fontSize: 20, letterSpacing: '-0.02em' }}>{org.name}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <div style={{ display: 'flex', gap: 24, fontSize: 14, fontWeight: 600, color: '#9ca3af' }}>
              {<a href="#classes" style={{ color: primary }}>Cours</a>}
              <a href="#plans"   style={{ color: '#9ca3af' }}>Tarifs</a>
              {products.length > 0 && <a href="#shop" style={{ color: '#9ca3af' }}>Boutique</a>}
              <a href="#contact" style={{ color: '#9ca3af' }}>Contact</a>
              <LoginLink orgId={org.id} color={primary} />
            </div>
            <PhoneButton phone={org.phone} primary={primary} dark />
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ maxWidth: 1152, margin: '0 auto', padding: '112px 24px' }}>
        <p style={{ color: primary, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 12 }}>{org.name}</p>
        <h1 style={{ fontSize: 64, fontWeight: 900, lineHeight: 0.95, textTransform: 'uppercase', marginBottom: 24, whiteSpace: 'pre-line' }}>
          {org.heroTitle || 'Entraîne-toi dur.\nObtiens des résultats.'}
        </h1>
        {org.heroSubtitle && <p style={{ color: '#9ca3af', fontSize: 18, maxWidth: 560, marginBottom: 32 }}>{org.heroSubtitle}</p>}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a href="#plans" style={{ background: primary, color: '#000', display: 'inline-block', padding: '14px 32px', borderRadius: 8, fontWeight: 900, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Rejoindre →
          </a>
          {org.trialCtaUrl && (
            <a href={ctaHref} style={{ display: 'inline-block', padding: '14px 32px', borderRadius: 8, fontWeight: 900, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', border: `1px solid ${primary}`, color: primary }}>
              {ctaLabel}
            </a>
          )}
        </div>
      </section>

      {/* Classes */}
      {sessions.length > 0 && (
        <section id="classes" style={{ borderTop: '1px solid #1f2937', padding: '80px 0' }}>
          <div style={{ maxWidth: 1152, margin: '0 auto', padding: '0 24px' }}>
            <p style={{ color: primary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Planning</p>
            <h3 style={{ fontSize: 32, fontWeight: 900, marginBottom: 40 }}>Prochaines séances</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
              {sessions.slice(0, 9).map(s => (
                <div key={s.id} style={{ border: '1px solid #1f2937', borderRadius: 12, padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 22 }}>{CATEGORY_ICONS[s.category] ?? '⚡'}</span>
                    <span style={{ color: primary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{s.category}</span>
                  </div>
                  <p style={{ fontWeight: 900 }}>{s.className}</p>
                  <p style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>{formatDate(s.date)} · {s.startTime}</p>
                  <p style={{ color: '#374151', fontSize: 12, marginTop: 4 }}>{s.durationMinutes} min · {s.bookedCount ?? 0}/{s.maxCapacity}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Plans */}
      {plans.length > 0 && (
        <section id="plans" style={{ borderTop: '1px solid #1f2937', padding: '80px 0' }}>
          <div style={{ maxWidth: 1152, margin: '0 auto', padding: '0 24px' }}>
            <p style={{ color: primary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Abonnement</p>
            <h3 style={{ fontSize: 32, fontWeight: 900, marginBottom: 40 }}>Accéder à la salle</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 16 }}>
              {plans.map((p, i) => (
                <div key={p.id} style={{ border: i === 1 ? `1px solid ${primary}` : '1px solid #1f2937', borderRadius: 12, padding: 24 }}>
                  {i === 1 && <p style={{ color: primary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Populaire</p>}
                  <p style={{ fontWeight: 900, fontSize: 20 }}>{p.name}</p>
                  <p style={{ fontSize: 40, fontWeight: 900, color: primary, marginTop: 12 }}>{p.price}
                    <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}> MAD/{formatDuration(p.durationDays, p.billingCycle)}</span>
                  </p>
                  {p.description && <p style={{ color: '#6b7280', fontSize: 13, marginTop: 12 }}>{p.description}</p>}
                  {org.phone && (
                    <a href={`tel:${org.phone}`} style={{ marginTop: 20, display: 'block', textAlign: 'center', padding: '10px 0', borderRadius: 8, fontWeight: 700, fontSize: 14, background: i === 1 ? primary : 'transparent', color: i === 1 ? '#000' : '#fff', border: i === 1 ? 'none' : '1px solid #374151' }}>
                      Commencer
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Shop */}
      {products.length > 0 && (
        <section id="shop" style={{ borderTop: '1px solid #1f2937', padding: '80px 0' }}>
          <div style={{ maxWidth: 1152, margin: '0 auto', padding: '0 24px' }}>
            <p style={{ color: primary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Boutique</p>
            <h3 style={{ fontSize: 32, fontWeight: 900, marginBottom: 40 }}>Disponible à la salle</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
              {products.map(p => (
                <div key={p.id} style={{ border: '1px solid #1f2937', borderRadius: 12, padding: 16, opacity: p.inStock ? 1 : 0.5 }}>
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.name} style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />
                  ) : (
                    <span style={{ fontSize: 22 }}>{PRODUCT_CATEGORY_ICONS[p.category] ?? '🛍️'}</span>
                  )}
                  <p style={{ fontWeight: 900, marginTop: 6 }}>{p.name}</p>
                  {p.brand && <p style={{ color: '#6b7280', fontSize: 12 }}>{p.brand}</p>}
                  <p style={{ color: primary, fontSize: 18, fontWeight: 900, marginTop: 4 }}>{p.price} <span style={{ fontSize: 11, color: '#6b7280' }}>MAD</span></p>
                  {!p.inStock && <p style={{ fontSize: 11, color: '#ef4444', fontWeight: 700, marginTop: 4 }}>Rupture de stock</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <ContactFooter org={org} primary={primary} dark />
    </div>
  )
}

// ── MINIMAL ─────────────────────────────────────────────────────────────────
function MinimalTemplate({ org, sessions, plans, products }) {
  const primary  = org.primaryColor ?? '#10b981'
  const ctaLabel = org.trialCtaLabel || 'Réserver un essai'
  const ctaHref  = org.trialCtaUrl  || `tel:${org.phone}`

  return (
    <div style={{ minHeight: '100vh', background: '#fafaf9', fontFamily: 'Inter, Segoe UI, Arial, sans-serif' }}>
      {/* Nav */}
      <nav style={{ maxWidth: 1024, margin: '0 auto', padding: '0 32px', height: 80, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <LogoBadge org={org} size={32} />
          <span style={{ fontWeight: 700, color: '#111' }}>{org.name}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ display: 'flex', gap: 24, fontSize: 14, color: '#6b7280' }}>
            {<a href="#classes" style={{ color: '#6b7280' }}>Cours</a>}
            <a href="#plans"   style={{ color: '#6b7280' }}>Forfaits</a>
            {products.length > 0 && <a href="#shop" style={{ color: '#6b7280' }}>Boutique</a>}
            <a href="#contact" style={{ color: '#6b7280' }}>Contact</a>
            <LoginLink orgId={org.id} color={primary} />
          </div>
          <PhoneButton phone={org.phone} primary={primary} />
        </div>
      </nav>

      {/* Hero */}
      <section style={{ maxWidth: 1024, margin: '0 auto', padding: '80px 32px' }}>
        <div style={{ maxWidth: 640 }}>
          <h1 style={{ fontSize: 40, fontWeight: 700, color: '#111', lineHeight: 1.3, marginBottom: 16 }}>{org.heroTitle || org.name}</h1>
          {org.heroSubtitle && <p style={{ color: '#6b7280', fontSize: 18, marginBottom: 24 }}>{org.heroSubtitle}</p>}
          {org.about && <p style={{ color: '#9ca3af', fontSize: 14, lineHeight: 1.8, marginBottom: 32, maxWidth: 480 }}>{org.about}</p>}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <a href="#plans" style={{ background: primary, display: 'inline-block', padding: '12px 24px', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 14 }}>
              Voir les forfaits
            </a>
            {org.trialCtaUrl && (
              <a href={ctaHref} style={{ display: 'inline-block', padding: '12px 24px', borderRadius: 8, color: primary, fontWeight: 600, fontSize: 14, border: `1px solid ${primary}` }}>
                {ctaLabel}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Classes */}
      {sessions.length > 0 && (
        <section id="classes" style={{ maxWidth: 1024, margin: '0 auto', padding: '64px 32px', borderTop: '1px solid #e7e5e4' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', marginBottom: 4 }}>Cours</h2>
          <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 24 }}>Planning à venir</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sessions.slice(0, 8).map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 16, background: '#fff', border: '1px solid #e7e5e4', borderRadius: 12, padding: '12px 20px' }}>
                <span style={{ fontSize: 20 }}>{CATEGORY_ICONS[s.category] ?? '⚡'}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{s.className}</p>
                  <p style={{ fontSize: 12, color: '#9ca3af' }}>{s.durationMinutes} min · {s.difficulty}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: 14, color: '#374151' }}>{formatDate(s.date)}</p>
                  <p style={{ fontSize: 12, color: '#9ca3af' }}>{s.startTime} · {s.bookedCount ?? 0}/{s.maxCapacity}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Plans */}
      {plans.length > 0 && (
        <section id="plans" style={{ maxWidth: 1024, margin: '0 auto', padding: '64px 32px', borderTop: '1px solid #e7e5e4' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', marginBottom: 24 }}>Abonnements</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 16 }}>
            {plans.map((p, i) => (
              <div key={p.id} style={{ background: '#fff', border: '1px solid #e7e5e4', borderRadius: 12, padding: 24 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{p.name}</p>
                <p style={{ fontSize: 32, fontWeight: 700, color: '#111', marginTop: 12 }}>{p.price} <span style={{ fontSize: 13, color: '#9ca3af', fontWeight: 400 }}>MAD</span></p>
                <p style={{ fontSize: 12, color: '#9ca3af' }}>par {formatDuration(p.durationDays, p.billingCycle)}</p>
                {p.description && <p style={{ fontSize: 13, color: '#6b7280', marginTop: 12 }}>{p.description}</p>}
                {org.phone && (
                  <a href={`tel:${org.phone}`} style={{ marginTop: 16, display: 'block', textAlign: 'center', padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, background: i === 0 ? primary : 'transparent', color: i === 0 ? '#fff' : '#374151', border: i === 0 ? 'none' : '1px solid #e7e5e4' }}>
                    En savoir plus
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Shop */}
      {products.length > 0 && (
        <section id="shop" style={{ maxWidth: 1024, margin: '0 auto', padding: '64px 32px', borderTop: '1px solid #e7e5e4' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', marginBottom: 24 }}>Boutique</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 16 }}>
            {products.map(p => (
              <div key={p.id} style={{ background: '#fff', border: '1px solid #e7e5e4', borderRadius: 12, padding: 16, opacity: p.inStock ? 1 : 0.5 }}>
                {p.imageUrl ? (
                  <img src={p.imageUrl} alt={p.name} style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />
                ) : (
                  <span style={{ fontSize: 20 }}>{PRODUCT_CATEGORY_ICONS[p.category] ?? '🛍️'}</span>
                )}
                <p style={{ fontSize: 14, fontWeight: 600, color: '#111', marginTop: 6 }}>{p.name}</p>
                {p.brand && <p style={{ fontSize: 12, color: '#9ca3af' }}>{p.brand}</p>}
                <p style={{ fontSize: 16, fontWeight: 700, color: '#111', marginTop: 6 }}>{p.price} <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>MAD</span></p>
                {!p.inStock && <p style={{ fontSize: 11, color: '#ef4444', fontWeight: 600, marginTop: 4 }}>Rupture de stock</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <ContactFooter org={org} primary={primary} />
    </div>
  )
}

// ── Root ─────────────────────────────────────────────────────────────────────
export default function App() {
  const { data, loading, error, slug } = useGymData()

  if (loading) return <LoadingScreen />
  if (error || !data) return <NotFound slug={slug} />

  const { org, sessions = [], plans = [], products = [] } = data
  const templateId = org.templateId ?? 'modern'

  if (templateId === 'sport')   return <SportTemplate   org={org} sessions={sessions} plans={plans} products={products} />
  if (templateId === 'minimal') return <MinimalTemplate org={org} sessions={sessions} plans={plans} products={products} />
  return <ModernTemplate org={org} sessions={sessions} plans={plans} products={products} />
}
